import { createSelector } from 'reselect';
import { VOCAB, TRO } from '../../constants/measure';
import { STATUS, PERIOD_TITLE, PERIOD } from '../../constants/result';
import { SUPPORTED_LANGUAGES } from '../../constants/language';
import playlistsHaveOnlineItem from '../utils/playlistUtils';

export const getPlaylistItems = state => {
    return state.items;
};

export const getIsPlaylistDataFetched = state => {
    return state.isDataFetched;
};

export const getAssessmentType = state => {
    return state.assessmentType;
};

export const getToy = state => {
    return state.toy;
};

export const getGrade = state => {
    return state.grade;
};

export const getInstId = (state, measure) => {
    const { measureInstIds } = state;
    return measureInstIds[measure];
};

export const getSchoolYear = state => {
    return state.schoolYear;
};

export const getStudentTemporalId = (state, instId) => {
    const { instTemporalIds } = state;
    return instTemporalIds[instId];
};

export const getRawPlaylistsByPeriod = state => {
    return state.rawPlaylistsByPeriod;
};

const getVocabPlaylistItems = (playlistItems, language) => {
    return playlistItems.filter(item => {
        return item.language === language && item.group === VOCAB.ASSESSMENT;
    });
};

const getVocabStatus = (playlistItems, language) => {
    const vocab = getVocabPlaylistItems(playlistItems, language);
    if (
        vocab.every(({ status }) => {
            return status === STATUS.NOT_STARTED;
        })
    ) {
        return STATUS.NOT_STARTED;
    }

    return vocab.every(({ status }) => {
        return status === STATUS.COMPLETED;
    })
        ? STATUS.COMPLETED
        : STATUS.IN_PROGRESS;
};

const getMeasureInProgress = playlistItems => {
    return playlistItems.find(item => {
        return item.status === STATUS.IN_PROGRESS;
    });
};

const getNotCompletedMeasure = playlistItems => {
    return playlistItems.find(item => {
        return item.status !== STATUS.COMPLETED;
    });
};

const getVocabInProgressMeasure = (playlistItems, language) => {
    const vocabPlaylistItems = getVocabPlaylistItems(playlistItems, language);

    return getMeasureInProgress(vocabPlaylistItems) || getNotCompletedMeasure(vocabPlaylistItems);
};

export const getCurrentToyFullText = createSelector(getToy, toy => {
    return PERIOD_TITLE[toy];
});

export const getPlaylistItemsForRender = createSelector(getPlaylistItems, playlistItems => {
    return playlistItems.reduce((accumulator, item) => {
        if (item.group !== VOCAB.ASSESSMENT) {
            accumulator.push(item);
        } else if (getVocabPlaylistItems(accumulator, item.language).length === 0) {
            accumulator.push({
                key: `vocab-${item.language}`,
                group: VOCAB.ASSESSMENT,
                name: item.name,
                language: item.language,
                status: getVocabStatus(playlistItems, item.language),
            });
        }
        return accumulator;
    }, []);
});

export const getPlaylistItemInProgress = createSelector(
    getPlaylistItemsForRender,
    playlistItems => {
        return getMeasureInProgress(playlistItems);
    },
);

export const getNextIncompleteMeasure = createSelector(
    [getPlaylistItems, getPlaylistItemInProgress],
    (playlistItems, playlistItemInProgress) => {
        let inProgressMeasure = playlistItemInProgress;
        if (inProgressMeasure && inProgressMeasure.group === VOCAB.ASSESSMENT) {
            inProgressMeasure = getVocabInProgressMeasure(
                playlistItems,
                inProgressMeasure.language,
            );
        }
        return inProgressMeasure || getNotCompletedMeasure(playlistItems);
    },
);

export const getUniquePlaylistMeasures = createSelector(getPlaylistItems, playlistItems => {
    return playlistItems
        .map(item => {
            return item.measures[0].measure;
        })
        .filter((measure, index, measures) => {
            return measures.indexOf(measure) === index;
        }, this);
});

export const getIsPlaylistCompleted = createSelector(getNextIncompleteMeasure, nextMeasure => {
    return nextMeasure === undefined;
});

export const getIsPlaylistInProgress = createSelector(
    [getIsPlaylistCompleted, getPlaylistItems],
    (isPlaylistCompleted, playlistItems) => {
        const allNotStarted =
            playlistItems.find(item => {
                return item.status === STATUS.IN_PROGRESS || item.status === STATUS.COMPLETED;
            }) === undefined;
        return !isPlaylistCompleted && !allNotStarted;
    },
);

export const getPlaylistActiveLanguages = createSelector(getPlaylistItems, playlistItems => {
    const languages = [];

    playlistItems.forEach(item => {
        if (!languages.includes(item.language) && SUPPORTED_LANGUAGES.includes(item.language)) {
            languages.push(item.language);
        }
    });

    return languages;
});

export const getLastCompletedMeasure = createSelector(
    [getPlaylistItems, getIsPlaylistCompleted],
    (playlistItems, isPlaylistCompleted) => {
        if (isPlaylistCompleted && playlistItems.length > 0) {
            return playlistItems[playlistItems.length - 1];
        }
        return undefined;
    },
);

export const getRecentlyCompletedMeasure = createSelector(
    [getPlaylistItems],
    playlistItems => playlistItems.find(item => item.isRecentlyCompleted) || { name: '' },
);

export const getActiveObsMeasures = createSelector(getPlaylistItems, playlistItems => {
    return playlistItems.reduce((list, item) => {
        if (!list.includes(item.type)) {
            list.push(item.type);
        }
        return list;
    }, []);
});

// Join this with getActiveObsMeasures to have only one method?
export const getActiveSecureObsMeasures = createSelector(getPlaylistItems, playlistItems => {
    return playlistItems
        .filter(item => item.secure)
        .reduce((list, item) => {
            if (!list.includes(item.type)) {
                list.push(item.type);
            }
            return list;
        }, []);
});

export const getIsTRCPracticeAvailable = createSelector(getNextIncompleteMeasure, nextMeasure => {
    return nextMeasure && nextMeasure.key === TRO.ASSESSMENT;
});

export const isAssessmentSecure = (state, measure) => {
    const measureItems = getPlaylistItems(state).filter(item => item.measure === measure);
    return measureItems.length > 0 && measureItems[0].secure;
};

export const hasSecureAssessments = state =>
    getPlaylistItems(state).some(({ secure, status }) => secure && status !== STATUS.COMPLETED);

export const isMeasureLicensed = (state, measure) =>
    getPlaylistItems(state).filter(item => item.measure === measure).length > 0;

export const getNextPlaylists = createSelector(
    [getToy, getIsPlaylistCompleted, getRawPlaylistsByPeriod],
    (toy, isPlaylistCompleted, rawPlaylistsByPeriod) => {
        if (isPlaylistCompleted) {
            switch (toy) {
                case undefined:
                    if (playlistsHaveOnlineItem(rawPlaylistsByPeriod[PERIOD.BOY]))
                        return rawPlaylistsByPeriod[PERIOD.BOY];
                // falls through
                case PERIOD.BOY:
                    if (playlistsHaveOnlineItem(rawPlaylistsByPeriod[PERIOD.MOY]))
                        return rawPlaylistsByPeriod[PERIOD.MOY];
                // falls through
                case PERIOD.MOY:
                    if (playlistsHaveOnlineItem(rawPlaylistsByPeriod[PERIOD.EOY]))
                        return rawPlaylistsByPeriod[PERIOD.EOY];
                // falls through
                case PERIOD.EOY:
                    if (playlistsHaveOnlineItem(rawPlaylistsByPeriod[PERIOD.SUMMER]))
                        return rawPlaylistsByPeriod[PERIOD.SUMMER];
                // falls through
                case PERIOD.SUMMER:
                    if (playlistsHaveOnlineItem(rawPlaylistsByPeriod[PERIOD.PM]))
                        return rawPlaylistsByPeriod[PERIOD.PM];
                // falls through
                default:
                    return rawPlaylistsByPeriod[toy];
            }
        }
        return rawPlaylistsByPeriod[toy];
    },
);

export const getHasNextToy = createSelector(
    [getToy, getRawPlaylistsByPeriod],
    (toy, rawPlaylistsByPeriod) => {
        switch (toy) {
            case undefined:
                if (playlistsHaveOnlineItem(rawPlaylistsByPeriod[PERIOD.BOY])) return true;
            // falls through
            case PERIOD.BOY:
                if (playlistsHaveOnlineItem(rawPlaylistsByPeriod[PERIOD.MOY])) return true;
            // falls through
            case PERIOD.MOY:
                if (playlistsHaveOnlineItem(rawPlaylistsByPeriod[PERIOD.EOY])) return true;
            // falls through
            case PERIOD.EOY:
                if (playlistsHaveOnlineItem(rawPlaylistsByPeriod[PERIOD.SUMMER])) return true;
            // falls through
            case PERIOD.SUMMER:
                if (playlistsHaveOnlineItem(rawPlaylistsByPeriod[PERIOD.PM])) return true;
            // falls through
            default:
                return false;
        }
    },
);
